
import { ListQuery } from "@/models/ListQuery";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { Deserializer } from "jsonapi-serializer";
import { genericError } from "@/models/GenericError";
import PresetCustomField, { PresetCustomFieldWrapper } from "@/models/PresetCustomField";
import { buildPreset } from "./presetService";
import { Translator } from "@/contexts/translator";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "@/constants/response-status-codes";
import { DeleteEntityResult } from "@/models/services/DeleteEntityResult";
import { BioEventAction, BioEventActionWrapper } from "@/models/BioEventAction";
const apiConnector = new APIConnector();

export const getAllBioEventActions = async (listParameters?: ListQuery): Promise<BioEventActionWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `bioEventAction?include=bioAddress,setting.settingType,eventType,setting.settingDatas.longMessage${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status,  message: getConstraintViolation(json.errors[0].detail)});
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const bioEventActions: BioEventAction[] = data.map((action) => {
                    const settingDatas: any[] = Array.isArray(action['setting']?.['setting-datas'])
                        ? action['setting']['setting-datas']
                        : [];

                    const getSettingDataMessage = (settingData: any): string => {
                        const nestedLongMessage = settingData?.['long-message'] ?? settingData?.longMessage;
                        const message =
                            nestedLongMessage?.['lm-message']
                            ?? settingData?.['lm-message']
                            ?? '';

                        return typeof message === 'string' ? message : '';
                    };

                    const message = settingDatas
                        .map((settingData: any) => getSettingDataMessage(settingData))
                        .find((value: string) => !!value) ?? '';

                    return {
                        id: action["id"],
                        settingId: action['setting']["id"],
                        name: action["setting"]["trn-name"],
                        eventTypeId: action['event-type']['id'],
                        type: action['setting']['setting-type']['trn-name'],
                        eventType: action['event-type']['trn-name'],
                        message
                    };
                });

                return resolve({ rows: bioEventActions, pagination });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getAllLCDMessageActions = async (translate: Translator, listParameters?: ListQuery, ): Promise<PresetCustomFieldWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const settingFields = 'fields[setting]=trnName,settingType,sDist,profile,settingIdToCopy';
        const settingTypeFields = 'fields[settingType]=trnName';
        const url = `setting?include=profile,settingType${paginationString}&${settingFields}&${settingTypeFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const presetAction: PresetCustomField[] = data.map(preset => buildPreset(preset, translate));

                return resolve({ rows: presetAction, pagination });
            })

        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const saveBioEventAction = async (terminalId: string, bioAddressId: string, eventTypeId: string, settingId: string) => {
    try {
        const newRecord = {
            data: {
                type: 'bioEventAction',
                relationships: {
                    terminal: {
                        data: {
                            type: 'terminal',
                            id: terminalId
                        }
                    },
                    bioAddress: {
                        data: { 
                            type: 'bioAddress', 
                            id: bioAddressId 
                        } 
                    },
                    eventType: {
                        data: {
                            type: 'eventType', 
                            id: eventTypeId 
                        } 
                    },
                    setting: { 
                        data: { 
                            type: 'setting', 
                            id: settingId 
                        } 
                    }
                }
            }
        };

        const url = 'bioEventAction';
        let response: Response;

        await apiConnector.sendData(url, true, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return Promise.reject(processServerError(error));
        });

        return new Promise<any>(async (resolve, reject) => {
            if (response) {
                const content = await response.json();
                if (content.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) });
                }
                new Deserializer().deserialize(content, (error) => {
                    if (error) {
                        return reject({ status: response.status, success: false, message: genericError });
                    }
                    return resolve({ success: true });
                });
                return;
            }
            return reject({ status: INTERNAL_SERVER_ERROR, success: false, message: genericError });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const deleteBioEventAction = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `bioEventAction/${id}`;
        const body = {
            data: {
                type: 'bioEventAction',
                id: id
            }
        };

        const response = await apiConnector.deleteData(url, body);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }
            const json = await response.json();
            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};